import React from 'react';
import { useComputedColorScheme, useMantineTheme } from '@mantine/core';
import { Group as VisxGroup } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { LinePath } from '@visx/shape';
import type { CashFlowPoint } from '../../pricing/mediaCalculator';

type Props = {
    data: CashFlowPoint[];
    breakEvenMonths: number | null;
};

const MARGIN = { top: 12, right: 20, bottom: 32, left: 64 };

function formatAxisCurrency(n: number): string {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}k`;
    return `${sign}$${Math.round(abs)}`;
}

const CashFlowChart: React.FC<Props> = ({ data, breakEvenMonths }) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');

    const lineColor = theme.colors.indigo[6];
    const positiveColor = theme.colors.teal[6];
    const zeroLineColor = colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[4];
    const axisColor = colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6];

    return (
        <div style={{ height: 280, width: '100%' }}>
            <ParentSize>
                {({ width, height }) => {
                    if (width < 10 || height < 10) return null;

                    const innerW = width - MARGIN.left - MARGIN.right;
                    const innerH = height - MARGIN.top - MARGIN.bottom;

                    const xMax = data[data.length - 1]?.month ?? 0;
                    const yValues = data.map((d) => d.cumulative);
                    const yMin = Math.min(0, ...yValues);
                    const yMax = Math.max(0, ...yValues);

                    const xScale = scaleLinear<number>({ domain: [0, xMax], range: [0, innerW] });
                    const yScale = scaleLinear<number>({
                        domain: [yMin, yMax],
                        range: [innerH, 0],
                        nice: true,
                    });

                    const zeroY = yScale(0);
                    const breakEvenX =
                        breakEvenMonths != null && breakEvenMonths <= xMax ? xScale(breakEvenMonths) : null;

                    return (
                        <svg width={width} height={height}>
                            <VisxGroup left={MARGIN.left} top={MARGIN.top}>
                                {/* Break-even zero line */}
                                <line
                                    x1={0}
                                    x2={innerW}
                                    y1={zeroY}
                                    y2={zeroY}
                                    stroke={zeroLineColor}
                                    strokeWidth={1}
                                    strokeDasharray="4 4"
                                />
                                {/* Break-even month marker */}
                                {breakEvenX != null && (
                                    <>
                                        <line
                                            x1={breakEvenX}
                                            x2={breakEvenX}
                                            y1={0}
                                            y2={innerH}
                                            stroke={positiveColor}
                                            strokeWidth={1}
                                            strokeDasharray="3 3"
                                            opacity={0.7}
                                        />
                                        <text
                                            x={breakEvenX + 4}
                                            y={12}
                                            fill={positiveColor}
                                            fontSize={11}
                                            fontWeight={600}
                                        >
                                            Break-even · mo {breakEvenMonths}
                                        </text>
                                    </>
                                )}
                                <LinePath<CashFlowPoint>
                                    data={data}
                                    x={(d) => xScale(d.month)}
                                    y={(d) => yScale(d.cumulative)}
                                    stroke={lineColor}
                                    strokeWidth={2.5}
                                />
                                <AxisLeft
                                    scale={yScale}
                                    numTicks={5}
                                    stroke={axisColor}
                                    tickStroke={axisColor}
                                    tickFormat={(v) => formatAxisCurrency(v as number)}
                                    tickLabelProps={() => ({
                                        fill: axisColor,
                                        fontSize: 10,
                                        textAnchor: 'end',
                                        dx: '-0.25em',
                                        dy: '0.25em',
                                    })}
                                />
                                <AxisBottom
                                    top={innerH}
                                    scale={xScale}
                                    numTicks={Math.max(2, Math.floor(innerW / 90))}
                                    stroke={axisColor}
                                    tickStroke={axisColor}
                                    tickFormat={(v) => `${v}mo`}
                                    tickLabelProps={() => ({
                                        fill: axisColor,
                                        fontSize: 10,
                                        textAnchor: 'middle',
                                    })}
                                />
                            </VisxGroup>
                        </svg>
                    );
                }}
            </ParentSize>
        </div>
    );
};

export default CashFlowChart;
